import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, Send, Info } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { FadeUp } from '../components/Reveal';
import { demandTypes, waUrl } from '../data/site';

const STEPS = ['Descreva a demanda', 'Revise a mensagem', 'Envie no WhatsApp'];

export default function Simulador() {
  const [searchParams] = useSearchParams();
  const initialDemand = demandTypes.includes(searchParams.get('demanda')) ? searchParams.get('demanda') : '';
  const [demandType, setDemandType] = useState(initialDemand);
  const [area, setArea] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const message = [
    'Olá, Eng. Amilcar! Acessei o site do Grupo Majo e gostaria de solicitar uma proposta técnica.',
    '',
    `*Tipo de demanda:* ${demandType || '—'}`,
    `*Área aproximada:* ${area ? `${area} m²` : '—'}`,
    `*Descrição:* ${description || '—'}`,
    '',
    'Aguardo retorno para alinharmos escopo, prazo e emissão de ART.',
  ].join('\n');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!demandType) {
      setError('Selecione o tipo de demanda para continuar.');
      return;
    }
    if (area && (isNaN(Number(area)) || Number(area) <= 0)) {
      setError('Informe uma área válida em m² (ou deixe em branco).');
      return;
    }
    setError('');
    window.open(waUrl(message), '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <PageHeader
        index="06"
        eyebrow="Simulador de Orçamento"
        title="Simulador rápido de demanda e orçamento"
        description="Preencha em menos de 1 minuto: geramos uma mensagem técnica formatada e abrimos direto no WhatsApp do Eng. Amilcar. Nenhum dado é armazenado."
      />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="simulator-section">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <FadeUp>
            <div className="flex flex-wrap gap-3" data-testid="simulator-steps">
              {STEPS.map((s, i) => (
                <span key={s} className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-xs font-semibold text-muted-c">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-gold font-display text-[10px] font-extrabold text-navy-deep">{i + 1}</span>
                  {s}
                </span>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6 rounded-3xl surface p-7 md:p-9" data-testid="simulator-form" noValidate>
              <div>
                <label htmlFor="demandType" className="mb-2 block font-display text-sm font-bold text-navy dark:text-white">
                  Tipo de demanda <span className="text-gold">*</span>
                </label>
                <select
                  id="demandType"
                  value={demandType}
                  onChange={(e) => { setDemandType(e.target.value); setError(''); }}
                  data-testid="simulator-demand-select"
                  className="w-full rounded-xl border border-line bg-[var(--bg)] px-4 py-3.5 text-sm outline-none transition-colors focus:border-gold"
                >
                  <option value="">Selecione o serviço desejado...</option>
                  {demandTypes.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="areaM2" className="mb-2 block font-display text-sm font-bold text-navy dark:text-white">
                  Área aproximada (m²)
                </label>
                <input
                  id="areaM2"
                  type="number"
                  min="1"
                  value={area}
                  onChange={(e) => { setArea(e.target.value); setError(''); }}
                  placeholder="Ex.: 220"
                  data-testid="simulator-area-input"
                  className="w-full rounded-xl border border-line bg-[var(--bg)] px-4 py-3.5 text-sm outline-none transition-colors focus:border-gold"
                />
              </div>

              <div>
                <label htmlFor="description" className="mb-2 block font-display text-sm font-bold text-navy dark:text-white">
                  Breve descrição do imóvel ou serviço
                </label>
                <textarea
                  id="description"
                  rows="4"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex.: salão comercial no térreo, preciso de laudo para adequar as instalações elétricas..."
                  data-testid="simulator-description-textarea"
                  className="w-full resize-none rounded-xl border border-line bg-[var(--bg)] px-4 py-3.5 text-sm outline-none transition-colors focus:border-gold"
                />
              </div>

              {error && (
                <p className="rounded-xl border border-red-300/60 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="simulator-error">
                  {error}
                </p>
              )}

              <button type="submit" className="btn-gold w-full" data-testid="simulator-submit-button">
                <Send size={16} /> Gerar mensagem e abrir no WhatsApp
              </button>

              <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-c" data-testid="simulator-privacy-note">
                <Info size={14} className="mt-0.5 shrink-0 text-gold" />
                Você será direcionado ao WhatsApp (11) 98256-1071 com a mensagem já pronta. Nenhuma informação é salva neste site.
              </p>
            </form>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="lg:sticky lg:top-28">
              <p className="mb-4 flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-gold">
                <MessageCircle size={15} /> Pré-visualização da mensagem
              </p>
              <div className="rounded-3xl border border-line bg-navy p-7 text-white shadow-[0_30px_70px_-30px_rgba(13,33,69,0.6)] md:p-9" data-testid="simulator-message-preview">
                <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-[#25D366] font-display text-sm font-extrabold text-white">A</span>
                  <div>
                    <p className="font-display text-sm font-bold">Eng. Amilcar Sottani Faccion</p>
                    <p className="text-xs text-white/50">Grupo Majo · online</p>
                  </div>
                </div>
                <div className="mt-5 whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-white/10 p-5 text-sm leading-relaxed">
                  {message}
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
